import React, {useEffect, useId, useRef, useState} from 'react';
import {SelectOption} from '../../../data/models/UI/SelectOption';
import {NA_OPTION} from '../../../data/staticValues/selectOptions';
import './styles.css';
import SearchInput from '../SearchInput';

type DropdownType = 'standart' | 'box';

interface Props {
  heading?: string;
  options: SelectOption[];
  placeholder?: string;
  selectedOption?: string | null;
  setSelectedOption: (option: string) => void;
  className?: string;
  withNA?: boolean;
  fieldError?: boolean;
  disabled?: boolean;
  isMandatory?: boolean;
  type?: DropdownType;
  subtitle?: string;
  onDropdownVisibilityToggle?: (isVisible: boolean) => void;
  enableSearch?: boolean;
  /** Names the control and makes it a keyboard-operable combobox/listbox. */
  ariaLabel?: string;
  /** With `ariaLabel`: the ids of the text that describes the control, such as its hint. */
  ariaDescribedBy?: string;
}

const Dropdown = React.memo(
  ({
    heading,
    options,
    placeholder,
    selectedOption,
    setSelectedOption,
    className,
    withNA,
    fieldError,
    disabled,
    isMandatory,
    type,
    subtitle,
    onDropdownVisibilityToggle,
    enableSearch,
    ariaLabel,
    ariaDescribedBy,
  }: Props) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    // The option of a labelled list that has focus: the list's only Tab stop (a roving
    // tabindex).
    const [activeKey, setActiveKey] = useState<string | null>(null);
    const listId = `${useId()}-listbox`;
    const openerRef = useRef<any>(null);
    const openerTextRef = useRef<any>(null);
    const openerArrowRef = useRef<any>(null);
    const optionRefs = useRef(new Map<string, HTMLLIElement>());

    useEffect(() => {
      window.addEventListener('click', handleBodyClick);
      return () => {
        window.removeEventListener('click', handleBodyClick);
      };
    }, [isOpen]);

    // A labelled list takes focus as it opens, on the selected option (or the first), unless
    // it has a search field to type in first.
    useEffect(() => {
      if (!ariaLabel || !isOpen || enableSearch) return;
      const start = startKey();
      if (start !== undefined) focusOption(start);
    }, [isOpen]);

    const handleBodyClick = (ev: any) => {
      if (
        isOpen &&
        openerRef?.current !== ev.target &&
        openerTextRef?.current !== ev.target &&
        openerArrowRef?.current !== ev.target
      ) {
        close();
      }
    };

    const getOptions = () => {
      let result = options;
      if (withNA) {
        result = [NA_OPTION, ...options];
      }
      if (enableSearch) {
        return result.filter(x =>
          x.value.toLowerCase().includes(search.toLowerCase()),
        );
      } else {
        return result;
      }
    };

    const open = () => {
      if (disabled || isOpen) return;
      if (onDropdownVisibilityToggle) {
        onDropdownVisibilityToggle(true);
      }
      setIsOpen(true);
    };

    /** Closes the list; `refocus` returns focus to the opener, as after a keyboard choice. */
    const close = (refocus = false) => {
      if (isOpen) {
        if (onDropdownVisibilityToggle) {
          onDropdownVisibilityToggle(false);
        }
        setIsOpen(false);
      }
      if (refocus) openerRef.current?.focus();
    };

    const toggle = () => (isOpen ? close() : open());

    const onOptionClicked = (value: string) => () => {
      setSelectedOption(value);
      // A labelled dropdown keeps focus once an option is chosen, as a native select does.
      close(!!ariaLabel);
    };

    const getSelectedOption = () => {
      return getOptions().find(option => option.key === selectedOption)?.value;
    };

    /** The option a labelled list starts on: the selected one, or else the first. */
    const startKey = () => {
      const shown = getOptions();
      return (shown.find(option => option.key === selectedOption) ?? shown[0])
        ?.key;
    };

    const focusOption = (key: string) => {
      setActiveKey(key);
      optionRefs.current.get(key)?.focus();
    };

    // Only labelled dropdowns are announced and keyboard-operable, so existing
    // unlabelled uses render exactly as before.
    const onOpenerKeyDown = (ev: React.KeyboardEvent) => {
      if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(ev.key)) {
        ev.preventDefault();
        if (!isOpen) {
          open();
        } else {
          const start = startKey();
          if (start !== undefined) focusOption(start);
        }
      } else if (ev.key === 'Escape' && isOpen) {
        close();
      }
    };
    const onOptionKeyDown = (key: string) => (ev: React.KeyboardEvent) => {
      const keys = getOptions().map(option => option.key);
      const at = keys.indexOf(key);
      const moveTo = (index: number) => {
        ev.preventDefault();
        focusOption(keys[Math.max(0, Math.min(keys.length - 1, index))]);
      };
      if (ev.key === 'Enter' || ev.key === ' ') {
        ev.preventDefault();
        onOptionClicked(key)();
      } else if (ev.key === 'Escape') {
        close(true);
      } else if (ev.key === 'ArrowDown') {
        moveTo(at + 1);
      } else if (ev.key === 'ArrowUp') {
        moveTo(at - 1);
      } else if (ev.key === 'Home') {
        moveTo(0);
      } else if (ev.key === 'End') {
        moveTo(keys.length - 1);
      } else if (ev.key === 'Tab') {
        // Tab moves on from the dropdown as a whole, as it would from its opener.
        close(true);
      }
    };
    // Focus leaving the opener and its list closes the list.
    const onContainerBlur = (ev: React.FocusEvent<HTMLDivElement>) => {
      if (!ev.currentTarget.contains(ev.relatedTarget as Node | null)) close();
    };

    const openerA11y = ariaLabel
      ? {
          role: 'combobox',
          tabIndex: disabled ? -1 : 0,
          'aria-label': ariaLabel,
          'aria-haspopup': 'listbox' as const,
          'aria-expanded': isOpen,
          'aria-controls': isOpen ? listId : undefined,
          'aria-describedby': ariaDescribedBy || undefined,
          'aria-disabled': disabled || undefined,
          onKeyDown: onOpenerKeyDown,
        }
      : {};
    const tabStop =
      activeKey !== null &&
      getOptions().some(option => option.key === activeKey)
        ? activeKey
        : startKey();
    const optionA11y = (option: SelectOption) =>
      ariaLabel
        ? {
            role: 'option',
            tabIndex: option.key === tabStop ? 0 : -1,
            'aria-selected': option.key === selectedOption,
            ref: (element: HTMLLIElement | null) => {
              if (element) optionRefs.current.set(option.key, element);
              else optionRefs.current.delete(option.key);
            },
            onKeyDown: onOptionKeyDown(option.key),
          }
        : {};
    const listA11y = ariaLabel
      ? {
          id: listId,
          role: 'listbox',
          'aria-label': ariaLabel,
          // A click on the list itself (its edge, its scrollbar) keeps focus within it.
          tabIndex: -1,
        }
      : {};

    const getRootClassByType = () => {
      switch (type) {
        case 'box':
          return 'DropdownBodyBox';
        default:
          return 'DropdownBody';
      }
    };

    return (
      <div className={`${className}`}>
        {heading && (
          <p className={`DropdownTitle ${disabled && 'DropdownDisabled'}`}>
            {heading}
            {isMandatory && !disabled ? (
              <span className="MandatoryIndicator">*</span>
            ) : (
              ''
            )}
          </p>
        )}
        {subtitle && <p className="DropdownSubtitleText">{subtitle}</p>}
        <div
          className="DropdownContainer"
          onBlur={ariaLabel ? onContainerBlur : undefined}>
          <div
            ref={openerRef}
            className={`${getRootClassByType()} ${
              !selectedOption ? 'Placeholder' : ''
            } ${fieldError ? 'FieldError' : ''} ${
              disabled && 'DropdownDisabled'
            }`}
            onClick={toggle}
            {...openerA11y}>
            <span ref={openerTextRef}>
              {getSelectedOption() || placeholder}
            </span>
            <span
              ref={openerArrowRef}
              className={
                isOpen ? 'icon-chevron-up' : 'icon-chevron-down'
              }></span>
          </div>
          {isOpen && (
            <ul
              {...listA11y}
              onClick={e => e.stopPropagation()}
              className={`DropdownList ${
                enableSearch ? 'DropdownListWithSearch' : ''
              }`}>
              {enableSearch && (
                <SearchInput
                  value={search}
                  onChange={setSearch}
                  className="DropdownSearch"
                />
              )}
              {getOptions().map(option => (
                <li
                  className="ListItem"
                  onClick={onOptionClicked(option.key)}
                  key={option.key}
                  {...optionA11y(option)}>
                  {option.value}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    );
  },
);

export default Dropdown;
